import { DOCUMENT } from '@angular/common';
import { Component, EventEmitter, HostListener, Inject, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute, ActivationEnd, Params, Router } from '@angular/router';
import { ScrollService } from '@app/core/scroll/scroll.service';
import { LinkService } from '@app/core/services/link.service';
import { WINDOW } from '@inshared/shared/util';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators';

@Component({
    selector: 'ins-paging',
    templateUrl: './paging.component.html',
    styleUrls: ['./paging.component.scss'],
})
export class PagingComponent implements OnChanges, OnDestroy {
    @Input() totalPages: number = 0;
    @Output() readonly pageSelected: EventEmitter<number> = new EventEmitter();

    public selectedPageNumber: number = 1;
    public shownPageNumbers: number[] = []; // max 10 at a time
    public location: Location;
    private maxAmountPageNumbersShown: number;
    private pageNumbersShownToTheLeftOfSelected: number;
    private href: string;

    private subscriptions: Subscription = new Subscription();

    constructor(
        private scrollService: ScrollService,
        private linkService: LinkService,
        private route: ActivatedRoute,
        private router: Router,
        @Inject(WINDOW) private window: Window,
        @Inject(DOCUMENT) document: Document,
    ) {
        this.location = document.location;
        this.href = `${this.location.origin}${this.location.pathname}`;
        this.subscriptions.add(this.subscribeOnRouterEvents());
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.totalPages && !changes.totalPages.firstChange) {
            this.updateShownPageNumbers();
            this.addMetaPagination();
        }
    }

    public updatePagination(params: Params): void {
        const pageNumber: number = +params.p || 1;

        this.selectedPageNumber = pageNumber;
        this.pageSelected.emit(pageNumber);
        this.updateShownPageNumbers();
        if (params.p) {
            this.scrollService.goToClass('.review-list');
        }
        this.addMetaPagination();
    }

    @HostListener('window:resize', ['$event'])  updateShownPageNumbers(): void {
        let maxNumbersToTheLeft: number; // the max amount of page numbers that are shown to the left of the current selected page number

        if (this.window.innerWidth <= 576) {
            this.maxAmountPageNumbersShown = 4;
            maxNumbersToTheLeft = 2;
        } else {
            this.maxAmountPageNumbersShown = 9;
            maxNumbersToTheLeft = 4;
        }

        this.pageNumbersShownToTheLeftOfSelected = Math.max(maxNumbersToTheLeft,
            this.maxAmountPageNumbersShown - (this.totalPages - this.selectedPageNumber));

        this.setShownPageNumbers();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    private subscribeOnRouterEvents(): Subscription {
        const obs$ = this.router.events.pipe(
            filter((event) => event instanceof ActivationEnd),
            map((event: ActivationEnd) => ({ ...event.snapshot.queryParams })),
            debounceTime(300),
            distinctUntilChanged(),
        );
        const obs = obs$.subscribe((params: Params) => this.updatePagination(params));
        this.subscriptions.add(obs);
        return obs;
    }

    private setShownPageNumbers(): void {
        const startIndex: number = Math.max(this.selectedPageNumber - this.pageNumbersShownToTheLeftOfSelected, 1);
        const endIndex: number = Math.min(startIndex + this.maxAmountPageNumbersShown, this.totalPages);

        this.shownPageNumbers = [];
        for (let i = startIndex; i <= endIndex; i++) {
            this.shownPageNumbers.push(i);
        }
    }

    private addMetaPagination(): void {
        const previous: number = this.selectedPageNumber - 1;
        const next: number = this.selectedPageNumber + 1;

        if (next >= this.totalPages) {
            if (previous > 0) {
                this.linkService.updateTag( { rel: 'prev', href: `${this.href}?p=${previous}` }, `rel="prev"` );
            } else {
                this.linkService.removeTag(`rel="prev"`);
            }
            this.linkService.removeTag(`rel="next"`);
        } else if (this.selectedPageNumber <= 1) {
            if (this.totalPages > 1) {
                this.linkService.updateTag( { rel: 'next', href: `${this.href}?p=${next}` }, `rel="next"` );
            } else {
                this.linkService.removeTag(`rel="next"`);
            }
            this.linkService.removeTag(`rel="prev"`);
        } else {
            this.linkService.updateTag( { rel: 'prev', href: `${this.href}?p=${previous}` }, `rel="prev"` );
            this.linkService.updateTag( { rel: 'next', href: `${this.href}?p=${next}` }, `rel="next"` );
        }
    }
}
